import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/ProductImage";

const TYPES = ["image/jpeg", "image/png"];
const MAX_SIDE = 1200;

// Reduz a foto no navegador (máx. 1200px) antes de enviar: upload leve e sem estourar limite do proxy
export const shrinkImage = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 400 * 1024) return resolve(file);
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const png = file.type === "image/png";
    c.toBlob((b) => resolve(b ? new File([b], file.name.replace(/\.[^.]+$/, png ? ".png" : ".jpg"), { type: png ? "image/png" : "image/jpeg" }) : file), png ? "image/png" : "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

// Campo "Imagem do produto": clique ou arraste JPG/PNG; prévia; trocar/remover. Só grava ao salvar a ficha.
export const ImageUpload = ({ recipe, file, removed, onFile, onRemove }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const pick = (f) => {
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error("Use uma imagem JPG ou PNG");
    if (f.size > 15 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 15 MB)");
    onFile(f);
  };
  const hasExisting = !!recipe?.image?.path && !removed;
  const has = !!file || hasExisting;
  return (
    <div className="space-y-2" data-testid="recipe-image-field">
      <p className="text-sm font-medium">Imagem do produto</p>
      <div role="button" tabIndex={0} onClick={() => inputRef.current?.click()} onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`rounded-2xl border-2 border-dashed p-3 flex items-center gap-4 cursor-pointer transition-colors ${drag ? "border-primary bg-primary/10" : "border-border hover:border-primary/60 hover:bg-secondary/40"}`} data-testid="recipe-image-dropzone">
        <ProductImage recipe={removed ? null : recipe} src={preview} size="lg" testId="recipe-image-preview" />
        <div className="min-w-0 text-sm">
          <p className="font-medium">{has ? (file ? "Nova imagem selecionada" : "Imagem cadastrada") : "Adicionar imagem"}</p>
          <p className="text-xs text-muted-foreground">{file ? file.name : "Clique ou arraste uma foto JPG/PNG (a foto é otimizada automaticamente)."} {has ? "Clique para trocar." : "Ela aparece na Precificação, nas Fichas e nos Combos."}</p>
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="recipe-image-input" />
      </div>
      {has && (
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid="recipe-image-change"><ImageUp className="w-3.5 h-3.5 mr-1" />Trocar</Button>
          <Button type="button" size="sm" variant="ghost" className="text-rose-500 hover:text-rose-500" onClick={onRemove} data-testid="recipe-image-remove"><Trash2 className="w-3.5 h-3.5 mr-1" />Remover</Button>
        </div>
      )}
    </div>
  );
};
